import { ExternalHyperlink, ImageRun, Paragraph, TextRun } from 'docx'
import {
  DraftQuestionItem,
  DraftTextCardItem,
  MediaType
} from '../../data/model/questionnaire'
import {
  getImageDimensionsAsync,
  getScaledImageDimensions
} from '../imageDimensions'
import { MEDIA_IMAGE_LABEL, MEDIA_VIDEO_PREFIX } from './model'

const MAX_THUMBNAIL_WIDTH = 150
const MAX_THUMBNAIL_HEIGHT = 150
const FULL_SIZE_IMAGE_ARGUMENT = 's0'

/**
 * The formats docx can embed directly.
 *
 * The uploader accepts `image/*` and the rendered URL is served with `=s0`,
 * meaning original size and original format, so a webp or avif genuinely can
 * reach this code — do not assume the resize service normalises anything.
 * Guessing a type for those would embed bytes under a content type that does
 * not match them, which Word renders as a broken image, so they are linked
 * instead (see `addImageThumbnail`). SVG is excluded deliberately: docx can
 * embed it, but only with a raster fallback we have no way to produce here.
 */
type EmbeddableImageType = 'png' | 'jpg' | 'gif' | 'bmp'

const EMBEDDABLE_IMAGE_TYPES: Record<string, EmbeddableImageType | undefined> =
  {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/gif': 'gif',
    'image/bmp': 'bmp'
  }

const imageTypeFromMagicBytes = (
  data: ArrayBuffer
): EmbeddableImageType | undefined => {
  const [b0, b1, b2, b3] = new Uint8Array(data.slice(0, 4))

  if (b0 === 0xff && b1 === 0xd8) return 'jpg'
  if (b0 === 0x47 && b1 === 0x49 && b2 === 0x46) return 'gif'
  if (b0 === 0x42 && b1 === 0x4d) return 'bmp'
  if (b0 === 0x89 && b1 === 0x50 && b2 === 0x4e && b3 === 0x47) return 'png'

  return undefined
}

const buildMediaLink = (mediaUrl: string, prefix = ''): Paragraph =>
  new Paragraph({
    children: [
      new ExternalHyperlink({
        children: [
          ...(prefix ? [new TextRun(prefix)] : []),
          new TextRun({ text: mediaUrl, style: 'Hyperlink' })
        ],
        link: mediaUrl
      })
    ]
  })

const addImageThumbnail = async (mediaUrl: string): Promise<Paragraph[]> => {
  const response = await fetch(mediaUrl)
  const blob = await response.blob()
  const data = await blob.arrayBuffer()

  const questionImageTextParagraph = new Paragraph({
    children: [new TextRun(MEDIA_IMAGE_LABEL)]
  })

  // Content-Type first because the server states it authoritatively; magic
  // bytes only cover the case where it is missing or generic.
  const imageType =
    EMBEDDABLE_IMAGE_TYPES[blob.type] ?? imageTypeFromMagicBytes(data)

  if (!imageType) {
    return [questionImageTextParagraph, buildMediaLink(mediaUrl)]
  }

  const { height, width } = await getImageDimensionsAsync(mediaUrl)
  const { height: scaledHeight, width: scaledWidth } = getScaledImageDimensions(
    width,
    height,
    MAX_THUMBNAIL_WIDTH,
    MAX_THUMBNAIL_HEIGHT
  )

  const mediaInfoParagraph = new Paragraph({
    children: [
      new ImageRun({
        type: imageType,
        data,
        transformation: {
          width: scaledWidth,
          height: scaledHeight
        }
      })
    ]
  })

  return [questionImageTextParagraph, mediaInfoParagraph]
}

const addVideoUrl = (mediaUrl: string): Paragraph =>
  buildMediaLink(mediaUrl, MEDIA_VIDEO_PREFIX)

export const addMediaIfNeeded = async (
  entryItem: DraftQuestionItem | DraftTextCardItem,
  paragraphs: Paragraph[]
) => {
  if (entryItem.questionMedia) {
    const { mediaType, mediaUrl, renderedMediaUrl } = entryItem.questionMedia

    if (mediaType === MediaType.Image) {
      paragraphs.push(
        ...(await addImageThumbnail(
          `${renderedMediaUrl}=${FULL_SIZE_IMAGE_ARGUMENT}`
        ))
      )
      // @todo Legacy eslint violation – fix this when editing
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (mediaType === MediaType.Video) {
      paragraphs.push(addVideoUrl(mediaUrl))
    }
  }
}
